/**
 * App-level keepalive + half-open detection for a WebSocket.
 *
 * Node's WebSocket (and the server) won't promptly report a connection that died
 * silently — an idle NAT/proxy timeout drops the TCP without a close frame, so
 * the socket sits in `OPEN` forever and `send()` never throws. We defend against
 * that by pinging the server on an interval (it answers "pong") and treating the
 * connection as dead if no frame arrives within {@link CONNECTION_SILENCE_TIMEOUT_MS}.
 *
 * The owner calls `start()` once the socket opens, `markAlive()` on every frame
 * it receives, and `stop()` when the socket closes. `onDead` fires at most once
 * per heartbeat and should drop + reconnect the socket.
 */

/** How often to ping the server (which replies "pong"). */
export const HEARTBEAT_INTERVAL_MS = 5000;

/**
 * If the server sends nothing — no pong, no data — for this long, the socket is
 * dead even if `readyState` still says OPEN. Kept comfortably under the server's
 * own liveness timeout so the client detects the drop and reconnects first.
 */
export const CONNECTION_SILENCE_TIMEOUT_MS = 15000;

export interface HeartbeatOptions {
  /** Ping cadence in ms. Defaults to {@link HEARTBEAT_INTERVAL_MS}. */
  intervalMs?: number;
  /** Silence-before-dead window in ms. Defaults to {@link CONNECTION_SILENCE_TIMEOUT_MS}. */
  silenceMs?: number;
}

export class Heartbeat {
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastRecvAt = 0;
  private readonly intervalMs: number;
  private readonly silenceMs: number;

  constructor(
    private ws: Pick<WebSocket, "readyState" | "send" | "close">,
    private onDead: () => void,
    options: HeartbeatOptions = {}
  ) {
    this.intervalMs = options.intervalMs ?? HEARTBEAT_INTERVAL_MS;
    this.silenceMs = options.silenceMs ?? CONNECTION_SILENCE_TIMEOUT_MS;
  }

  start(): void {
    this.stop();
    this.lastRecvAt = Date.now();
    this.timer = setInterval(() => this.tick(), this.intervalMs);
  }

  /** Record that a frame was received — proof the connection is alive. */
  markAlive(): void {
    this.lastRecvAt = Date.now();
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private tick(): void {
    if (Date.now() - this.lastRecvAt > this.silenceMs) {
      this.fail();
      return;
    }
    try {
      if (this.ws.readyState === WebSocket.OPEN) this.ws.send("ping");
      else this.fail();
    } catch {
      this.fail();
    }
  }

  private fail(): void {
    this.stop();
    try {
      this.ws.close();
    } catch {
      // already closing/closed
    }
    this.onDead();
  }
}
